import { IAppContext } from 'App/AppContext';
import { TFunction } from 'i18next';
import Joi from 'joi';
import TaskTypeFormElement from 'models/tasks/taskTypeFormElement';
import { apiGetTaskTypeName } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import { getLocalizedMessageOptions } from 'services/Localization/joiValidation';
import { truncate } from 'utils/string';
import { TaskType_Translation } from './taskType_Translation';

export enum SystemTaskTypes {
  None = 0,
  Approval = 1,
  DPIA = 2,
  ProcessingActivityApproval = 3,
}

// The kind of a task: the server's BaseTask.TaskKinds, mirrored by hand. The server stamps it when the
// task is created and never changes it; it drives which UI SingleTask dispatches to. Values 1-3 equal
// SystemTaskTypes; InternalAudit covers the procedure library of an audit series and its execution tasks;
// AssetInquiry and OutcomeInquiry are the Kickstart inquiry tasks; Assessment assesses one entity against its
// active audit criteria set (PLAN-075).
export enum TaskKinds {
  None = 0,
  Approval = 1,
  DPIA = 2,
  ProcessingActivityApproval = 3,
  InternalAudit = 4,
  AssetInquiry = 5,
  OutcomeInquiry = 6,
  Assessment = 7,
}

export default class TaskType {
  static readonly tabNameMaxLength = 32;

  taskTypeId: number;

  taskTypeFamilyId?: number;

  version: number;

  definitionHash?: string;

  firstUsedOn?: Date;

  lastAmendedOn?: Date;

  rowVersion?: string;

  isArchived: boolean;

  authSchemaId?: number;

  systemTaskType?: number;

  isSystem: boolean;

  // Set when creating a form that belongs to one audit procedure: such a form is not a catalogue
  // entry and is never offered where a form is picked. Never read back - a hidden form is not
  // returned by the form reads at all.
  isHidden?: boolean;

  trans: TaskType_Translation[];

  transIdx: number;

  name: string;

  description?: string;

  tabName?: string;

  form?: TaskTypeFormElement[];

  constructor() {
    this.taskTypeId = 0;
    this.trans = [];
    this.name = '';
    this.transIdx = -1;
    this.isSystem = false;
    this.version = 0;
    this.isArchived = false;
  }

  static getSystemTypeText(systemTaskType: SystemTaskTypes, t: TFunction<string[]>) {
    switch (systemTaskType) {
      case SystemTaskTypes.Approval:
        return t('forms:SystemTypes.Approval');
      case SystemTaskTypes.DPIA:
        return t('forms:SystemTypes.DPIA');
      case SystemTaskTypes.ProcessingActivityApproval:
        return t('forms:SystemTypes.ProcessingActivityApproval');
      default:
        return '';
    }
  }

  // The form an audit procedure asks its criteria on is GENERATED, and therefore hidden. The login
  // fills the task-type cache with the query filters off, so a hidden form IS in it after login -
  // but GET tasks/types applies the IsHidden filter, so a cache refreshed from there loses it, and
  // the cache's get() answers a miss with an empty stand-in (taskTypeId 0). That stand-in is why the
  // tab had no name and why opening the editor loaded nothing - it asked the server for form 0.
  // (The form pickers filter isHidden themselves, see TaskTopTasksBar.getForms.)
  //
  // Resolve against the cache first, and fall back to a stand-in carrying the REAL id and a generic
  // label. Everything downstream (the form read, the editor) only needs the id; the label is for the
  // tab and the editor's title.
  static forTask(
    taskTypeId: number | undefined,
    appContext: IAppContext,
    t: TFunction<string[]>,
    taskTypeFamilyId?: number,
    exactVersion?: TaskType,
  ): TaskType | undefined {
    if (!taskTypeId) return undefined;
    if (exactVersion?.taskTypeId === taskTypeId) return exactVersion;

    const fromCache = appContext.globalDataCache.taskTypes.get(taskTypeId);
    if (fromCache.taskTypeId === taskTypeId) return fromCache;

    const generated = new TaskType();
    generated.taskTypeId = taskTypeId;
    generated.taskTypeFamilyId = taskTypeFamilyId;
    generated.name = t('forms:View.GeneratedFormName');
    generated.tabName = generated.name;

    return generated;
  }

  // The form tab is named after the tab name of the form version the task is bound to. The cache holds
  // the current head of an active family only, so it cannot name a superseded version, an archived
  // family or a form created after the cache was filled: the version travelling on the task wins over
  // it. Forms from before the tab name was derived from the form name have none - then the form name
  // names the tab, shortened to what a tab name may hold.
  static tabNameForTask(
    taskTypeId: number | undefined,
    formVersion: TaskType | undefined,
    appContext: IAppContext,
  ): string | undefined {
    if (!taskTypeId) return undefined;

    const taskType =
      formVersion?.taskTypeId === taskTypeId ? formVersion : appContext.globalDataCache.taskTypes.get(taskTypeId);

    if (taskType.tabName) return taskType.tabName;

    return taskType.name ? truncate(taskType.name, TaskType.tabNameMaxLength) : undefined;
  }

  getTransIdx(langCode: string, defLangCode: string): number {
    if (this.trans) {
      //find requested language
      for (let i = 0; i < this.trans.length; i++) {
        const t: TaskType_Translation = this.trans[i];
        if (t.lang?.code === langCode) {
          return i;
        }
      }

      //if not found, find the default language
      for (let i = 0; i < this.trans.length; i++) {
        const t: TaskType_Translation = this.trans[i];
        if (t.lang?.code === defLangCode) {
          return i;
        }
      }
    }

    return -1;
  }

  clone(): TaskType {
    const output = new TaskType();
    output.taskTypeId = this.taskTypeId;
    output.taskTypeFamilyId = this.taskTypeFamilyId;
    output.version = this.version;
    output.definitionHash = this.definitionHash;
    output.firstUsedOn = this.firstUsedOn ? new Date(this.firstUsedOn) : undefined;
    output.lastAmendedOn = this.lastAmendedOn ? new Date(this.lastAmendedOn) : undefined;
    output.rowVersion = this.rowVersion;
    output.isArchived = this.isArchived;
    output.systemTaskType = this.systemTaskType;
    output.isSystem = this.isSystem;
    output.description = this.description;
    output.name = this.name;
    output.transIdx = this.transIdx;
    output.trans = [...this.trans];
    output.authSchemaId = this.authSchemaId;
    output.tabName = this.tabName;
    output.isHidden = this.isHidden;

    return output;
  }

  isEqual(item: TaskType): boolean {
    if (item.name !== this.name) return false;
    if (item.description !== this.description) return false;
    if (item.systemTaskType !== this.systemTaskType) return false;
    if (item.taskTypeId !== this.taskTypeId) return false;
    if (item.taskTypeFamilyId !== this.taskTypeFamilyId) return false;
    if (item.version !== this.version) return false;
    if (item.definitionHash !== this.definitionHash) return false;
    if (item.isArchived !== this.isArchived) return false;
    if (item.isSystem !== this.isSystem) return false;
    if (item.authSchemaId !== this.authSchemaId) return false;
    if (item.tabName !== this.tabName) return false;

    return true;
  }

  validate(localizedFields: Record<string, string>): Joi.ValidationResult {
    const schema: Joi.ObjectSchema = Joi.object({
      name: Joi.string().max(512).required().label(localizedFields['name']),
      tabname: Joi.string().max(TaskType.tabNameMaxLength).label(localizedFields['tabname']),
    }).prefs(getLocalizedMessageOptions());

    return schema.validate(
      {
        name: this.name,
      },
      { abortEarly: false },
    );
  }

  static async nameExists(orgName: string, appContext: IAppContext): Promise<number> {
    const accessToken = await appContext.getAccessToken(apiRequest.scopes);

    return await apiGetTaskTypeName(orgName, accessToken);
  }
}
